import "./style.css";
import { installFinanceView } from "./finance";
import { installProviderApplicationsView } from "./providers";

type ProductStatus = "DRAFT" | "PENDING_REVIEW" | "PUBLISHED" | "ARCHIVED";
type ProductPaymentMode = "EXACT" | "BATCH";

type Product = {
  id: string;
  providerId: string;
  slug: string;
  name: string;
  description: string;
  serviceUrl: string;
  method: string;
  providerType: "GENERIC" | "WINSHIELD";
  providerAction: string;
  requestEncoding: "JSON" | "QUERY" | "MULTIPART";
  requestSchema: string;
  requestTemplate: string;
  responseSchema: string;
  paymentMode: ProductPaymentMode;
  network: string;
  tokenAddress: string;
  priceUsdcAtomic: string;
  costUsdcAtomic: string;
  receiverWallet: string;
  status: ProductStatus;
  version: number;
  updatedAt: string;
  workingVersion?: ProductVersion;
};

type ProductVersion = {
  id: string;
  productId: string;
  baseVersion: number;
  version: number;
  status: ProductStatus;
  definition: Product;
  submittedAt?: string;
  updatedAt: string;
};

type Order = {
  id: string;
  clientRef?: string;
  payerWallet?: string;
  status: string;
  paymentState: string;
  serviceState: string;
  quote: {
    name: string;
    priceUsdcAtomic: string;
    costUsdcAtomic: string;
    network: string;
    receiverWallet: string;
  };
  createdAt: string;
};

type OrderPagination = {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
};

type OrderSummary = {
  totalCalls: number;
  deliveredCalls: number;
  failedCalls: number;
  settledOrders: number;
  totalRevenueAtomic: string;
  totalCostAtomic: string;
  grossProfitAtomic: string;
};

type OrderCustomerSummary = {
  customerWallet: string;
  productId: string;
  productName: string;
  callCount: number;
  deliveredCount: number;
  failedCount: number;
  settledCount: number;
  totalRevenueAtomic: string;
  totalCostAtomic: string;
  grossProfitAtomic: string;
};

type BatchChannel = {
  channelId: string;
  payer: string;
  receiver: string;
  token: string;
  balance: string;
  chargedCumulativeAmount: string;
  totalClaimed: string;
  availableAmount: string;
  claimableAmount: string;
  withdrawRequestedAt: number;
  pending: boolean;
};

type BatchSettings = {
  refundHoldingSeconds: number;
  updatedAt: string;
};

type NotificationSubscription = {
  id: string;
  productId: string;
  productName: string;
  orderId: string;
  payerWallet: string;
  status: string;
  quotaTotal: number;
  quotaRemaining: number;
  balanceTotalAtomic: string;
  balanceRemainingAtomic: string;
  terminationReason?: string;
  activationCallbackStatus?: string;
  terminationCallbackStatus?: string;
  activatedAt?: string;
  createdAt: string;
  updatedAt: string;
};

type AdminPermission = "products" | "product_edit" | "integrations" | "orders" | "revenue" | "providers" | "provider_review" | "access" | "audit";
type AdminView = "products" | "providers" | "orders" | "subscriptions" | "revenue" | "settlement" | "finance" | "access" | "audit";

const ADMIN_VIEWS: AdminView[] = ["products", "providers", "orders", "subscriptions", "revenue", "settlement", "finance", "access", "audit"];
const VIEW_PERMISSIONS: Record<AdminView, AdminPermission> = {
  products: "products",
  providers: "providers",
  orders: "orders",
  subscriptions: "orders",
  revenue: "revenue",
  settlement: "revenue",
  finance: "revenue",
  access: "access",
  audit: "audit",
};

type AdminUser = {
  id: string;
  name: string;
  email: string;
  enabled: boolean;
  permissions: AdminPermission[];
  createdAt: string;
  updatedAt: string;
};

type AuditFieldChange = { before: unknown; after: unknown };

type AuditLog = {
  id: string;
  actorId: string;
  actorName: string;
  actorEmail: string;
  action: string;
  resourceType: string;
  resourceId: string;
  resourceName: string;
  changes: Record<string, AuditFieldChange>;
  createdAt: string;
};

type ProductProviderBinding = {
  productId: string;
  environment: string;
  credentialId: string;
  alias: string;
  serviceType: string;
  providerAction: string;
  endpointUrl: string;
  enabled: boolean;
  active: boolean;
  hasUsername: boolean;
  hasPassword: boolean;
  hasSecret: boolean;
  version: number;
  status: string;
  source?: string;
  lastTestedAt?: string;
  lastTestStatus: string;
  updatedAt: string;
};

type Simulation = {
  id: string;
  productId: string;
  productName: string;
  providerType: string;
  action?: string;
  environment?: string;
  configVersion?: number;
  state: string;
  httpStatus?: number;
  durationMs: number;
  errorCode?: string;
  errorMessage?: string;
  createdAt: string;
};

type ParameterOption = { label: string; value: string };

type ParameterDefinition = {
  name: string;
  label: string;
  type: "string" | "textarea" | "integer" | "number" | "boolean" | "select" | "chain" | "date" | "datetime" | "file" | "json";
  required: boolean;
  placeholder?: string;
  helpText?: string;
  defaultValue?: string;
  options?: ParameterOption[];
  accept?: string;
  maxBytes?: number;
};

type ResponseFieldDefinition = {
  path: string;
  label: string;
  format: "text" | "number" | "boolean" | "datetime" | "link" | "json";
};

type ResponseDisplayDefinition = {
  mode: "JSON" | "KEY_VALUE" | "LIST" | "TABLE" | "TEXT";
  root?: string;
  fields?: ResponseFieldDefinition[];
  summaryRoot?: string;
  summaryFields?: ResponseFieldDefinition[];
};

const COMMON_CHAIN_OPTIONS: ParameterOption[] = [
  { label: "Ethereum (ETH) · 全量查询", value: "1" },
  { label: "Bitcoin (BTC) · 全量查询", value: "0" },
  { label: "BNB Smart Chain (BSC) · 全量查询", value: "56" },
  { label: "TRON · 全量查询", value: "79" },
  { label: "Polygon · 全量查询", value: "137" },
  { label: "Avalanche · 全量查询", value: "43114" },
  { label: "Arbitrum · 全量查询", value: "42161" },
  { label: "Optimism · 全量查询", value: "10" },
  { label: "Solana · 全量查询", value: "solana" },
  { label: "Aptos · 全量查询", value: "aptos" },
  { label: "TON · 全量查询", value: "ton" },
  { label: "XRP Ledger · 全量查询", value: "xrp" },
  { label: "Litecoin (LTC) · 全量查询", value: "227" },
  { label: "zkSync · 全量查询", value: "324" },
  { label: "Base · 基础查询", value: "8453" },
];

const BASE_SEPOLIA_NETWORK = "eip155:84532";
const BASE_SEPOLIA_USDC = "0x036CbD53842c5426634e7929541eC2318f3dCF7e";

function formatOptions(options: ParameterOption[]): string {
  return options.map(option => `${option.label}:${option.value}`).join(",");
}

const apiBase = import.meta.env.VITE_API_BASE_URL || "http://127.0.0.1:8080";
const providerAPIDocsURL = `${apiBase.replace(/\/+$/, "")}/provider-integration/docs`;

document.querySelectorAll<HTMLAnchorElement>("[data-provider-api-link]").forEach(link => {
  link.href = providerAPIDocsURL;
});
let token = sessionStorage.getItem("bizx402-admin-token") || "";
let products: Product[] = [];
let orders: Order[] = [];
let notificationSubscriptions: NotificationSubscription[] = [];
let productProviderBinding: ProductProviderBinding | undefined;
let currentUser: AdminUser | undefined;
let adminUsers: AdminUser[] = [];
let auditLogs: AuditLog[] = [];
let orderFilterProducts: Array<{ id: string; name: string }> = [];
let orderPagination: OrderPagination = { page: 1, pageSize: 20, total: 0, totalPages: 0 };
let subscriptionPagination: OrderPagination = { page: 1, pageSize: 20, total: 0, totalPages: 0 };
let auditPagination: OrderPagination = { page: 1, pageSize: 20, total: 0, totalPages: 0 };
let revenueSummary: OrderSummary = {
  totalCalls: 0, deliveredCalls: 0, failedCalls: 0, settledOrders: 0, totalRevenueAtomic: "0",
  totalCostAtomic: "0", grossProfitAtomic: "0",
};
let revenueByCustomer: OrderCustomerSummary[] = [];
let batchChannels: BatchChannel[] = [];
let batchSettings: BatchSettings = { refundHoldingSeconds: 300, updatedAt: "" };
let simulationRawResult: unknown;
let simulationResponseSchema = "";
let simulationResultAvailable = false;
let simulationShowingRaw = false;
let simulationTrigger: HTMLElement | null = null;
let simulationProviderBinding: ProductProviderBinding | undefined;
let simulationScrollY = 0;

const loginView = document.querySelector<HTMLElement>("#login-view")!;
const adminView = document.querySelector<HTMLElement>("#admin-view")!;
const loginForm = document.querySelector<HTMLFormElement>("#login-form")!;
const productForm = document.querySelector<HTMLFormElement>("#product-form")!;
const simulationForm = document.querySelector<HTMLFormElement>("#simulation-form")!;
const simulationModal = document.querySelector<HTMLElement>("#simulation-modal")!;
const userForm = document.querySelector<HTMLFormElement>("#user-form")!;
const productRows = document.querySelector<HTMLElement>("#product-rows")!;
const orderRows = document.querySelector<HTMLElement>("#order-rows")!;
const subscriptionRows = document.querySelector<HTMLElement>("#subscription-rows")!;
const userRows = document.querySelector<HTMLElement>("#user-rows")!;
const auditRows = document.querySelector<HTMLElement>("#audit-rows")!;
const parameterRows = document.querySelector<HTMLElement>("#parameter-rows")!;
const responseFieldRows = document.querySelector<HTMLElement>("#response-field-rows")!;
const responseSummaryFieldRows = document.querySelector<HTMLElement>("#response-summary-field-rows")!;
const simulationFields = document.querySelector<HTMLElement>("#simulation-fields")!;
const orderProductFilter = document.querySelector<HTMLSelectElement>("#order-product-filter")!;
const orderStatusFilter = document.querySelector<HTMLSelectElement>("#order-status-filter")!;
const orderDateFrom = document.querySelector<HTMLInputElement>("#order-date-from")!;
const orderDateTo = document.querySelector<HTMLInputElement>("#order-date-to")!;
const revenueDateFrom = document.querySelector<HTMLInputElement>("#revenue-date-from")!;
const revenueDateTo = document.querySelector<HTMLInputElement>("#revenue-date-to")!;
const orderPageSize = document.querySelector<HTMLSelectElement>("#order-page-size")!;
const subscriptionProductFilter = document.querySelector<HTMLSelectElement>("#subscription-product-filter")!;
const subscriptionStatusFilter = document.querySelector<HTMLSelectElement>("#subscription-status-filter")!;
const subscriptionWalletFilter = document.querySelector<HTMLInputElement>("#subscription-wallet-filter")!;
const subscriptionPageSize = document.querySelector<HTMLSelectElement>("#subscription-page-size")!;
const auditActorFilter = document.querySelector<HTMLInputElement>("#audit-actor-filter")!;
const auditResourceFilter = document.querySelector<HTMLSelectElement>("#audit-resource-filter")!;
const auditDateFrom = document.querySelector<HTMLInputElement>("#audit-date-from")!;
const auditDateTo = document.querySelector<HTMLInputElement>("#audit-date-to")!;
const auditPageSize = document.querySelector<HTMLSelectElement>("#audit-page-size")!;
const batchChannelRows = document.querySelector<HTMLElement>("#batch-channel-rows")!;
const batchRefundHoldingMinutes =
  document.querySelector<HTMLInputElement>("#batch-refund-holding-minutes")!;

function escapeHTML(value: string): string {
  return value.replace(/[&<>"']/g, character => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#039;",
  })[character]!);
}

function parseParameterDefinitions(raw: string): ParameterDefinition[] {
  try {
    const parsed = JSON.parse(raw || "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parseResponseDefinition(raw: string): ResponseDisplayDefinition {
  try {
    const parsed = JSON.parse(raw || "{}") as ResponseDisplayDefinition;
    return {
      mode: parsed.mode || "JSON",
      root: parsed.root || "",
      fields: parsed.fields || [],
      summaryRoot: parsed.summaryRoot || "",
      summaryFields: parsed.summaryFields || [],
    };
  } catch {
    return { mode: "JSON", root: "", fields: [] };
  }
}

function parameterTypeOptions(selected: string): string {
  const types = [
    ["string", "单行文本"], ["textarea", "多行文本"], ["integer", "整数"],
    ["number", "数字"], ["boolean", "布尔开关"], ["select", "下拉选择"],
    ["chain", "区块链"],
    ["date", "日期"], ["datetime", "日期时间"], ["file", "文件"],
    ["json", "JSON"],
  ];
  return types.map(([value, label]) =>
    `<option value="${value}"${selected === value ? " selected" : ""}>${label}</option>`,
  ).join("");
}

function responseFormatOptions(selected: string): string {
  return ["text", "number", "boolean", "datetime", "link", "json"].map(value =>
    `<option value="${value}"${selected === value ? " selected" : ""}>${value}</option>`,
  ).join("");
}

function renderParameterDefinitions(definitions: ParameterDefinition[]): void {
  parameterRows.innerHTML = definitions.map((item, index) => `
    <div class="definition-card" data-parameter-row>
      <div class="definition-number">${String(index + 1).padStart(2, "0")}</div>
      <div class="definition-fields">
        <label class="field"><span>参数名</span><input data-param="name" value="${escapeHTML(item.name || "")}" placeholder="例如 address" required /></label>
        <label class="field"><span>显示名称</span><input data-param="label" value="${escapeHTML(item.label || "")}" placeholder="例如 钱包地址" required /></label>
        <label class="field"><span>参数类型</span><select data-param="type">${parameterTypeOptions(item.type || "string")}</select></label>
        <label class="check-field"><input data-param="required" type="checkbox"${item.required ? " checked" : ""} /><span>必填</span></label>
        <label class="field"><span>默认值</span><input data-param="defaultValue" value="${escapeHTML(item.defaultValue || "")}" /></label>
        <label class="field"><span>占位提示</span><input data-param="placeholder" value="${escapeHTML(item.placeholder || "")}" /></label>
        <label class="field wide-field"><span>帮助说明</span><input data-param="helpText" value="${escapeHTML(item.helpText || "")}" /></label>
        <label class="field option-field"><span>选项（标签:值，逗号分隔）</span><input data-param="options" value="${escapeHTML(formatOptions(item.options || []))}" /></label>
        <label class="field file-field"><span>允许文件类型</span><input data-param="accept" value="${escapeHTML(item.accept || "")}" placeholder=".pdf,image/*" /></label>
        <label class="field file-field"><span>最大字节数</span><input data-param="maxBytes" type="number" min="1" max="8388608" value="${item.maxBytes || 8388608}" /></label>
      </div>
      <button class="remove-definition" data-remove-parameter type="button">删除</button>
    </div>
  `).join("");
  document.querySelector<HTMLElement>("#parameter-empty")!.hidden = definitions.length !== 0;
  parameterRows.querySelectorAll<HTMLElement>("[data-parameter-row]").forEach(row => {
    const type = row.querySelector<HTMLSelectElement>("[data-param=type]")!;
    const options = row.querySelector<HTMLInputElement>("[data-param=options]")!;
    const update = () => {
      if (type.value === "chain" && options.value.trim() === "") {
        options.value = formatOptions(COMMON_CHAIN_OPTIONS);
      }
      row.querySelectorAll<HTMLElement>(".option-field").forEach(element => element.hidden = type.value !== "select" && type.value !== "chain");
      row.querySelectorAll<HTMLElement>(".file-field").forEach(element => element.hidden = type.value !== "file");
    };
    type.addEventListener("change", update);
    update();
  });
  parameterRows.querySelectorAll<HTMLButtonElement>("[data-remove-parameter]").forEach(button => {
    button.addEventListener("click", () => {
      button.closest("[data-parameter-row]")?.remove();
      renumberDefinitions(parameterRows);
      document.querySelector<HTMLElement>("#parameter-empty")!.hidden =
        parameterRows.querySelector("[data-parameter-row]") !== null;
    });
  });
}

function renumberDefinitions(container: HTMLElement): void {
  container.querySelectorAll<HTMLElement>(".definition-number").forEach((element, index) => {
    element.textContent = String(index + 1).padStart(2, "0");
  });
}

function readParameterDefinitions(): ParameterDefinition[] {
  return Array.from(parameterRows.querySelectorAll<HTMLElement>("[data-parameter-row]")).map(row => {
    const value = (name: string) => row.querySelector<HTMLInputElement>(`[data-param=${name}]`)?.value.trim() || "";
    const type = row.querySelector<HTMLSelectElement>("[data-param=type]")!.value as ParameterDefinition["type"];
    const options = value("options").split(",").map(item => item.trim()).filter(Boolean).map(item => {
      const separator = item.indexOf(":");
      return separator < 0
        ? { label: item, value: item }
        : { label: item.slice(0, separator).trim(), value: item.slice(separator + 1).trim() };
    });
    const maxBytes = Number(value("maxBytes"));
    return {
      name: value("name"),
      label: value("label"),
      type,
      required: row.querySelector<HTMLInputElement>("[data-param=required]")!.checked,
      defaultValue: value("defaultValue") || undefined,
      placeholder: value("placeholder") || undefined,
      helpText: value("helpText") || undefined,
      options: type === "select" || type === "chain" ? options : undefined,
      accept: type === "file" ? value("accept") || undefined : undefined,
      maxBytes: type === "file" ? maxBytes || 8388608 : undefined,
    };
  });
}

function renderResponseFields(fields: ResponseFieldDefinition[]): void {
  responseFieldRows.innerHTML = fields.map((field, index) => `
    <div class="response-field-row" data-response-field-row>
      <span class="definition-number">${String(index + 1).padStart(2, "0")}</span>
      <label class="field"><span>字段路径</span><input data-response="path" value="${escapeHTML(field.path || "")}" placeholder="例如 riskLevel 或 rules.0.day" /></label>
      <label class="field"><span>显示名称</span><input data-response="label" value="${escapeHTML(field.label || "")}" /></label>
      <label class="field"><span>格式</span><select data-response="format">${responseFormatOptions(field.format || "text")}</select></label>
      <button class="remove-definition" data-remove-response type="button">删除</button>
    </div>
  `).join("");
  responseFieldRows.querySelectorAll<HTMLButtonElement>("[data-remove-response]").forEach(button => {
    button.addEventListener("click", () => {
      button.closest("[data-response-field-row]")?.remove();
      renumberDefinitions(responseFieldRows);
    });
  });
}

function renderResponseSummaryFields(fields: ResponseFieldDefinition[]): void {
  responseSummaryFieldRows.innerHTML = fields.map((field, index) => `
    <div class="response-field-row" data-response-summary-field-row>
      <span class="definition-number">${String(index + 1).padStart(2, "0")}</span>
      <label class="field"><span>字段路径</span><input data-response="path" value="${escapeHTML(field.path || "")}" placeholder="例如 finalScore" /></label>
      <label class="field"><span>显示名称</span><input data-response="label" value="${escapeHTML(field.label || "")}" /></label>
      <label class="field"><span>格式</span><select data-response="format">${responseFormatOptions(field.format || "text")}</select></label>
      <button class="remove-definition" data-remove-response-summary type="button">删除</button>
    </div>
  `).join("");
  responseSummaryFieldRows.querySelectorAll<HTMLButtonElement>("[data-remove-response-summary]").forEach(button => {
    button.addEventListener("click", () => {
      button.closest("[data-response-summary-field-row]")?.remove();
      renumberDefinitions(responseSummaryFieldRows);
    });
  });
}

function readResponseFields(container: HTMLElement, rowSelector: string): ResponseFieldDefinition[] {
  return Array.from(container.querySelectorAll<HTMLElement>(rowSelector)).map(row => ({
    path: row.querySelector<HTMLInputElement>("[data-response=path]")!.value.trim(),
    label: row.querySelector<HTMLInputElement>("[data-response=label]")!.value.trim(),
    format: row.querySelector<HTMLSelectElement>("[data-response=format]")!.value as ResponseFieldDefinition["format"],
  }));
}

function readResponseDefinition(): ResponseDisplayDefinition {
  return {
    mode: (document.querySelector<HTMLSelectElement>("#response-mode")!).value as ResponseDisplayDefinition["mode"],
    root: (document.querySelector<HTMLInputElement>("#response-root")!).value.trim() || undefined,
    fields: readResponseFields(responseFieldRows, "[data-response-field-row]"),
    summaryRoot: (document.querySelector<HTMLInputElement>("#response-summary-root")!).value.trim() || undefined,
    summaryFields: readResponseFields(responseSummaryFieldRows, "[data-response-summary-field-row]"),
  };
}

function defaultTemplate(definitions: ParameterDefinition[]): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const item of definitions) {
    if (item.defaultValue === undefined || item.defaultValue === "") continue;
    if (item.type === "number") values[item.name] = Number(item.defaultValue);
    else if (item.type === "integer") values[item.name] = Number.parseInt(item.defaultValue, 10);
    else if (item.type === "boolean") values[item.name] = item.defaultValue === "true";
    else if (item.type !== "file") values[item.name] = item.defaultValue;
  }
  return values;
}

function renderRuntimeForm(container: HTMLElement, definitions: ParameterDefinition[], legacyTemplate = "{}"): void {
  if (definitions.length === 0) {
    container.innerHTML = `<label class="field"><span>请求 JSON</span><textarea data-runtime-legacy rows="10" spellcheck="false">${escapeHTML(formatJSONText(legacyTemplate))}</textarea></label>`;
    return;
  }
  container.innerHTML = definitions.map(item => {
    const required = item.required ? " required" : "";
    const placeholder = item.placeholder ? ` placeholder="${escapeHTML(item.placeholder)}"` : "";
    let control = `<input data-runtime-name="${escapeHTML(item.name)}" type="text" value="${escapeHTML(item.defaultValue || "")}"${placeholder}${required} />`;
    if (item.type === "textarea" || item.type === "json") {
      control = `<textarea data-runtime-name="${escapeHTML(item.name)}" rows="4"${placeholder}${required}>${escapeHTML(item.defaultValue || "")}</textarea>`;
    } else if (item.type === "integer" || item.type === "number") {
      control = `<input data-runtime-name="${escapeHTML(item.name)}" type="number" step="${item.type === "integer" ? "1" : "any"}" value="${escapeHTML(item.defaultValue || "")}"${placeholder}${required} />`;
    } else if (item.type === "boolean") {
      control = `<label class="runtime-check"><input data-runtime-name="${escapeHTML(item.name)}" type="checkbox"${item.defaultValue === "true" ? " checked" : ""} /><span>启用</span></label>`;
    } else if (item.type === "select" || item.type === "chain") {
      control = `<select data-runtime-name="${escapeHTML(item.name)}"${required}>${(item.options || []).map(option => `<option value="${escapeHTML(option.value)}"${item.defaultValue === option.value ? " selected" : ""}>${escapeHTML(option.label)}</option>`).join("")}</select>`;
    } else if (item.type === "date" || item.type === "datetime") {
      control = `<input data-runtime-name="${escapeHTML(item.name)}" type="${item.type === "date" ? "date" : "datetime-local"}" value="${escapeHTML(item.defaultValue || "")}"${required} />`;
    } else if (item.type === "file") {
      control = `<input data-runtime-name="${escapeHTML(item.name)}" type="file"${item.accept ? ` accept="${escapeHTML(item.accept)}"` : ""}${required} />`;
    }
    return `<label class="field runtime-field"><span>${escapeHTML(item.label)}${item.required ? " *" : ""}</span>${control}${item.helpText ? `<small>${escapeHTML(item.helpText)}</small>` : ""}</label>`;
  }).join("");
}

async function collectRuntimeValues(container: HTMLElement, definitions: ParameterDefinition[]): Promise<Record<string, unknown>> {
  const legacy = container.querySelector<HTMLTextAreaElement>("[data-runtime-legacy]");
  if (legacy) return JSON.parse(legacy.value) as Record<string, unknown>;
  const values: Record<string, unknown> = {};
  for (const item of definitions) {
    const control = container.querySelector<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(`[data-runtime-name="${CSS.escape(item.name)}"]`)!;
    if (item.type === "file") {
      const file = (control as HTMLInputElement).files?.[0];
      if (!file) {
        if (item.required) throw new Error(`${item.label} 为必填文件。`);
        continue;
      }
      const maxBytes = item.maxBytes || 8388608;
      if (file.size > maxBytes) throw new Error(`${item.label} 超过 ${maxBytes} 字节限制。`);
      values[item.name] = {
        name: file.name,
        contentType: file.type || "application/octet-stream",
        base64: await fileToBase64(file),
      };
      continue;
    }
    if (item.type === "boolean") {
      values[item.name] = (control as HTMLInputElement).checked;
      continue;
    }
    const raw = control.value.trim();
    if (!raw) {
      if (item.required) throw new Error(`${item.label} 为必填参数。`);
      continue;
    }
    if (item.type === "number") values[item.name] = Number(raw);
    else if (item.type === "integer") values[item.name] = Number.parseInt(raw, 10);
    else if (item.type === "json") values[item.name] = JSON.parse(raw);
    else values[item.name] = raw;
  }
  return values;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`读取文件 ${file.name} 失败。`));
    reader.onload = () => resolve(String(reader.result).split(",", 2)[1] || "");
    reader.readAsDataURL(file);
  });
}

function valueAtPath(source: unknown, path: string): unknown {
  if (!path) return source;
  return path.split(".").reduce<unknown>((current, key) => {
    if (current && typeof current === "object") return (current as Record<string, unknown>)[key];
    return undefined;
  }, source);
}

function formatDisplayValue(value: unknown, format: string): string {
  if (value === null || value === undefined) return "—";
  if (format === "boolean") return value ? "是" : "否";
  if (format === "datetime") {
    const date = new Date(String(value));
    return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString("zh-CN");
  }
  if (format === "json" || typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function renderedKeyValues(source: unknown, fields: ResponseFieldDefinition[]): string {
  return `<dl class="rendered-key-values">${fields.map(field => {
    const value = valueAtPath(source, field.path);
    const rendered = formatDisplayValue(value, field.format);
    return `<div><dt>${escapeHTML(field.label)}</dt><dd class="${field.format === "json" ? "json-value" : ""}">${escapeHTML(rendered)}</dd></div>`;
  }).join("")}</dl>`;
}

function renderConfiguredResponse(container: HTMLElement, result: unknown, rawSchema: string): void {
  const definition = parseResponseDefinition(rawSchema);
  const root = valueAtPath(result, definition.root || "");
  if (definition.mode === "TEXT") {
    container.innerHTML = `<p class="rendered-text">${escapeHTML(formatDisplayValue(root, "text"))}</p>`;
    return;
  }
  if (definition.mode === "KEY_VALUE") {
    container.innerHTML = renderedKeyValues(root, definition.fields || []);
    return;
  }
  if (definition.mode === "LIST") {
    const rows = Array.isArray(root) ? root : [];
    const summaryFields = definition.summaryFields || [];
    const summary = summaryFields.length === 0
      ? ""
      : `<section class="rendered-result-summary">
          <header><span>SUMMARY</span><strong>总体结果</strong></header>
          ${renderedKeyValues(valueAtPath(result, definition.summaryRoot || ""), summaryFields)}
        </section>`;
    container.innerHTML = rows.length === 0
      ? `${summary}<p class="rendered-empty">未提取到数组结果，请检查数据根路径。</p>`
      : `${summary}<div class="rendered-result-list">${rows.map((row, index) => `
          <section class="rendered-result-group">
            <header><span>RESULT</span><strong>记录 ${String(index + 1).padStart(2, "0")}</strong></header>
            ${renderedKeyValues(row, definition.fields || [])}
          </section>`).join("")}
        </div>`;
    return;
  }
  if (definition.mode === "TABLE") {
    const rows = Array.isArray(root) ? root : [];
    container.innerHTML = `<div class="rendered-table-wrap"><table><thead><tr>${(definition.fields || []).map(field => `<th>${escapeHTML(field.label)}</th>`).join("")}</tr></thead><tbody>${rows.map(row => `<tr>${(definition.fields || []).map(field => `<td>${escapeHTML(formatDisplayValue(valueAtPath(row, field.path), field.format))}</td>`).join("")}</tr>`).join("")}</tbody></table></div>`;
    return;
  }
  container.innerHTML = `<pre>${escapeHTML(JSON.stringify(result, null, 2))}</pre>`;
}

function renderSimulationResultView(): void {
  const container = document.querySelector<HTMLElement>("#simulation-result")!;
  const toggle = document.querySelector<HTMLButtonElement>("#toggle-raw-response")!;
  toggle.disabled = !simulationResultAvailable;
  toggle.textContent = simulationShowingRaw ? "按配置展示" : "查看原始 JSON";
  if (!simulationResultAvailable) {
    container.textContent = "尚未调用";
    return;
  }
  if (simulationShowingRaw) {
    container.innerHTML = `<pre>${escapeHTML(JSON.stringify(simulationRawResult, null, 2))}</pre>`;
    return;
  }
  renderConfiguredResponse(container, simulationRawResult, simulationResponseSchema);
}

function shortAddress(address: string): string {
  return address.length > 16 ? `${address.slice(0, 8)}…${address.slice(-6)}` : address;
}

function formatUSDC(atomic: string): string {
  let value: bigint;
  try {
    value = BigInt(atomic || "0");
  } catch {
    return "0";
  }
  const negative = value < 0n;
  const digits = (negative ? -value : value).toString();
  const padded = digits.padStart(7, "0");
  const whole = padded.slice(0, -6).replace(/^0+(?=\d)/, "");
  const fraction = padded.slice(-6).replace(/0+$/, "");
  return `${negative ? "-" : ""}${whole}${fraction ? `.${fraction}` : ""}`;
}

function usdcToAtomic(value: string): string {
  const normalized = value.trim();
  if (!/^\d+(?:\.\d{1,6})?$/.test(normalized)) {
    throw new Error("USDC 金额最多支持 6 位小数");
  }
  const [whole, fraction = ""] = normalized.split(".");
  return (BigInt(whole) * 1_000_000n + BigInt(fraction.padEnd(6, "0") || "0")).toString();
}

function subtractAtomic(left: string, right: string): string {
  try {
    return (BigInt(left || "0") - BigInt(right || "0")).toString();
  } catch {
    return "0";
  }
}

function sumAtomic(values: string[]): string {
  return values.reduce((total, value) => {
    try {
      return total + BigInt(value || "0");
    } catch {
      return total;
    }
  }, 0n).toString();
}

async function api<T>(path: string, options: RequestInit = {}): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body) headers.set("content-type", "application/json");
  if (token) headers.set("authorization", `Bearer ${token}`);
  const response = await fetch(`${apiBase}${path}`, { ...options, headers });
  const body = await response.json().catch(() => ({})) as T & { error?: { message: string } };
  if (!response.ok) {
    if (response.status === 401 && path !== "/v1/admin/login") showLogin();
    throw new Error(body.error?.message || `API 返回 ${response.status}`);
  }
  return body;
}

const financeView = installFinanceView({ api, formatUSDC, escapeHTML, shortAddress });
const providersView = installProviderApplicationsView({
  api,
  escapeHTML,
  canReview: () => hasPermission("provider_review"),
  onCountChange: count => {
    if (!document.querySelector<HTMLElement>("#providers-view")!.hidden) {
      document.querySelector("#summary-count")!.textContent = `${count} APPLICATIONS`;
    }
  },
});
document.querySelector<HTMLInputElement>("#product-receiver")?.closest("label")?.querySelector("span")?.replaceChildren("Provider 出款钱包");

function showLogin(): void {
  token = "";
  currentUser = undefined;
  sessionStorage.removeItem("bizx402-admin-token");
  loginView.hidden = false;
  adminView.hidden = true;
}

function hasPermission(permission: AdminPermission): boolean {
  return currentUser?.permissions.includes(permission) === true;
}

function canView(view: AdminView): boolean {
  return hasPermission(VIEW_PERMISSIONS[view]);
}

function showAdmin(): void {
  if (!currentUser) {
    showLogin();
    return;
  }
  loginView.hidden = true;
  adminView.hidden = false;
  document.querySelector("#current-user")!.textContent = `${currentUser.name} · ${currentUser.email}`;
  document.querySelectorAll<HTMLButtonElement>(".nav-item").forEach(item => {
    item.hidden = !hasPermission(item.dataset.permission as AdminPermission);
  });
  const requested = window.location.hash.replace(/^#\//, "") as AdminView;
  const view = ADMIN_VIEWS.includes(requested) && canView(requested)
    ? requested
    : ADMIN_VIEWS.find(canView)!;
  if (window.location.hash !== `#/${view}`) {
    window.history.replaceState(null, "", `#/${view}`);
  }
  renderAdminView(view);
}

function showStatus(elementId: string, message: string, error = false): void {
  const element = document.querySelector<HTMLElement>(`#${elementId}`)!;
  element.hidden = false;
  element.className = `form-status ${error ? "error" : "success"}`;
  element.textContent = message;
}

function renderProducts(): void {
  const canReviewProducts = hasPermission("product_edit");
  document.querySelector<HTMLElement>("#product-empty")!.hidden = products.length !== 0;
  document.querySelector("#summary-count")!.textContent = `${products.length} PRODUCTS`;
  productRows.innerHTML = products.map(product => {
    const pending = product.workingVersion?.status === "PENDING_REVIEW"
      ? product.workingVersion
      : undefined;
    const definition = pending?.definition || product;
    return `
      <tr>
        <td><strong>${escapeHTML(definition.name)}</strong><small>${escapeHTML(product.providerId || "未分配 Provider")} · ${escapeHTML(product.slug)} · ${escapeHTML(definition.providerType)}${definition.providerAction ? ` / ${escapeHTML(definition.providerAction)}` : ""} · ${pending ? `待审 v${pending.version} / 线上 v${product.version}` : `v${product.version}`}</small></td>
        <td><strong>${escapeHTML(formatUSDC(definition.priceUsdcAtomic))} USDC</strong><small>成本 ${escapeHTML(formatUSDC(definition.costUsdcAtomic || "0"))} USDC / CALL${pending ? " · 待审配置" : ""}</small></td>
        <td><code title="${escapeHTML(definition.receiverWallet)}">${escapeHTML(shortAddress(definition.receiverWallet))}</code></td>
        <td><span class="status-chip ${product.status.toLowerCase()}">${product.status}</span>${pending ? `<small>V${pending.version} · ${pending.status}</small>` : ""}</td>
        <td class="row-actions">
          <button type="button" data-simulate="${escapeHTML(product.id)}">测试${pending ? ` V${pending.version}` : ""}</button>
          ${canReviewProducts && (product.status === "PENDING_REVIEW" || pending) ? `<button type="button" data-toggle="${escapeHTML(product.id)}">审核发布${pending ? ` V${pending.version}` : ""}</button>` : ""}
          ${canReviewProducts && product.status === "PUBLISHED" && !pending ? `<button type="button" data-toggle="${escapeHTML(product.id)}">下架</button>` : ""}
        </td>
      </tr>
    `;
  }).join("");
  productRows.querySelectorAll<HTMLButtonElement>("[data-toggle]").forEach(button => {
    button.addEventListener("click", () => void toggleProduct(button.dataset.toggle!));
  });
  productRows.querySelectorAll<HTMLButtonElement>("[data-simulate]").forEach(button => {
    button.addEventListener("click", () => void openSimulation(button.dataset.simulate!, button));
  });
}

function renderOrderProductFilter(): void {
  const selectedProduct = orderProductFilter.value;
  const options = `<option value="">全部产品</option>${orderFilterProducts.map(product =>
    `<option value="${escapeHTML(product.id)}">${escapeHTML(product.name)}</option>`,
  ).join("")}`;
  orderProductFilter.innerHTML = options;
  orderProductFilter.value = selectedProduct;
  const selectedSubscriptionProduct = subscriptionProductFilter.value;
  subscriptionProductFilter.innerHTML = options;
  subscriptionProductFilter.value = selectedSubscriptionProduct;
}

function renderOrders(): void {
  document.querySelector<HTMLElement>("#order-empty")!.hidden = orders.length !== 0;
  orderRows.innerHTML = orders.map(order => `
    <tr>
      <td><strong>${escapeHTML(order.id)}</strong><small>${escapeHTML(order.quote.name)}${order.clientRef ? ` · ${escapeHTML(order.clientRef)}` : ""}</small></td>
      <td><strong>${order.payerWallet ? escapeHTML(shortAddress(order.payerWallet)) : "未识别"}</strong><small>${order.payerWallet ? escapeHTML(order.payerWallet) : "完成付款后记录客户钱包"}</small></td>
      <td><strong>${escapeHTML(formatUSDC(order.quote.priceUsdcAtomic))} USDC</strong><small>成本 ${escapeHTML(formatUSDC(order.quote.costUsdcAtomic || "0"))} · 毛利 ${escapeHTML(formatUSDC(subtractAtomic(order.quote.priceUsdcAtomic, order.quote.costUsdcAtomic || "0")))} USDC</small></td>
      <td><span class="status-chip">${escapeHTML(order.paymentState)}</span></td>
      <td><span class="status-chip">${escapeHTML(order.serviceState)}</span></td>
      <td><code>${escapeHTML(new Date(order.createdAt).toLocaleString("zh-CN"))}</code></td>
    </tr>
  `).join("");
  const totalPages = Math.max(1, orderPagination.totalPages);
  document.querySelector("#order-page-label")!.textContent =
    `第 ${orderPagination.page} / ${totalPages} 页 · 共 ${orderPagination.total} 笔`;
  (document.querySelector<HTMLButtonElement>("#order-prev-page")!).disabled = orderPagination.page <= 1;
  (document.querySelector<HTMLButtonElement>("#order-next-page")!).disabled =
    orderPagination.totalPages === 0 || orderPagination.page >= orderPagination.totalPages;
  if (!document.querySelector<HTMLElement>("#orders-view")!.hidden) {
    document.querySelector("#summary-count")!.textContent = `${orderPagination.total} ORDERS`;
  }
}

function renderNotificationSubscriptions(): void {
  document.querySelector<HTMLElement>("#subscription-empty")!.hidden =
    notificationSubscriptions.length !== 0;
  subscriptionRows.innerHTML = notificationSubscriptions.map(subscription => {
    const callback = subscription.terminationCallbackStatus
      ? `终止 ${subscription.terminationCallbackStatus}`
      : subscription.activationCallbackStatus
      ? `开通 ${subscription.activationCallbackStatus}`
      : "—";
    const activatedAt = subscription.activatedAt && !subscription.activatedAt.startsWith("0001-")
      ? subscription.activatedAt
      : subscription.createdAt;
    return `<tr>
      <td><strong>${escapeHTML(subscription.productName)}</strong><small>${escapeHTML(subscription.id)} · 订单 ${escapeHTML(subscription.orderId)}</small></td>
      <td><strong>${escapeHTML(shortAddress(subscription.payerWallet))}</strong><small>${escapeHTML(subscription.payerWallet)}</small></td>
      <td><strong>${subscription.quotaRemaining} / ${subscription.quotaTotal} 次</strong><small>每笔订阅独立额度</small></td>
      <td><strong>${escapeHTML(formatUSDC(subscription.balanceRemainingAtomic))} USDC</strong><small>购买 ${escapeHTML(formatUSDC(subscription.balanceTotalAtomic))} USDC</small></td>
      <td><span class="status-chip">${escapeHTML(subscription.status)}</span>${subscription.terminationReason ? `<small>${escapeHTML(subscription.terminationReason)}</small>` : ""}</td>
      <td><span class="status-chip">${escapeHTML(callback)}</span></td>
      <td><code>${escapeHTML(new Date(activatedAt).toLocaleString("zh-CN"))}</code></td>
    </tr>`;
  }).join("");
  const totalPages = Math.max(1, subscriptionPagination.totalPages);
  document.querySelector("#subscription-page-label")!.textContent =
    `第 ${subscriptionPagination.page} / ${totalPages} 页 · 共 ${subscriptionPagination.total} 笔`;
  (document.querySelector<HTMLButtonElement>("#subscription-prev-page")!).disabled =
    subscriptionPagination.page <= 1;
  (document.querySelector<HTMLButtonElement>("#subscription-next-page")!).disabled =
    subscriptionPagination.totalPages === 0 ||
    subscriptionPagination.page >= subscriptionPagination.totalPages;
  if (!document.querySelector<HTMLElement>("#subscriptions-view")!.hidden) {
    document.querySelector("#summary-count")!.textContent =
      `${subscriptionPagination.total} SUBSCRIPTIONS`;
  }
}

function renderRevenueReport(): void {
  document.querySelector<HTMLElement>("#order-summary-empty")!.hidden = revenueByCustomer.length !== 0;
  document.querySelector("#order-total-calls")!.textContent = String(revenueSummary.totalCalls);
  document.querySelector("#order-delivered-calls")!.textContent = String(revenueSummary.deliveredCalls);
  document.querySelector("#order-failed-calls")!.textContent = String(revenueSummary.failedCalls);
  document.querySelector("#order-settled-orders")!.textContent = String(revenueSummary.settledOrders);
  document.querySelector("#order-total-revenue")!.textContent = formatUSDC(revenueSummary.totalRevenueAtomic);
  document.querySelector("#order-total-cost")!.textContent = formatUSDC(revenueSummary.totalCostAtomic);
  document.querySelector("#order-gross-profit")!.textContent = formatUSDC(revenueSummary.grossProfitAtomic);
  document.querySelector("#order-summary-rows")!.innerHTML = revenueByCustomer.map(item => `
    <tr>
      <td><strong>${item.customerWallet ? escapeHTML(shortAddress(item.customerWallet)) : "未识别"}</strong><small>${item.customerWallet ? escapeHTML(item.customerWallet) : "未完成付款"}</small></td>
      <td><strong>${escapeHTML(item.productName)}</strong><small>${escapeHTML(item.productId)}</small></td>
      <td><strong>${item.callCount}</strong></td>
      <td><strong>${item.deliveredCount}</strong></td>
      <td><strong>${item.failedCount}</strong></td>
      <td><strong>${item.settledCount}</strong></td>
      <td><strong>${escapeHTML(formatUSDC(item.totalRevenueAtomic))} USDC</strong></td>
      <td><strong>${escapeHTML(formatUSDC(item.totalCostAtomic))} USDC</strong></td>
      <td><strong>${escapeHTML(formatUSDC(item.grossProfitAtomic))} USDC</strong></td>
    </tr>
  `).join("");
  if (!document.querySelector<HTMLElement>("#revenue-view")!.hidden) {
    document.querySelector("#summary-count")!.textContent =
      `${formatUSDC(revenueSummary.totalRevenueAtomic)} USDC`;
  }
}

function renderBatchChannels(): void {
  document.querySelector("#batch-channel-count")!.textContent = String(batchChannels.length);
  document.querySelector("#batch-balance-total")!.textContent =
    formatUSDC(sumAtomic(batchChannels.map(channel => channel.balance)));
  document.querySelector("#batch-available-total")!.textContent =
    formatUSDC(sumAtomic(batchChannels.map(channel => channel.availableAmount)));
  document.querySelector("#batch-claimable-total")!.textContent =
    formatUSDC(sumAtomic(batchChannels.map(channel => channel.claimableAmount)));
  document.querySelector<HTMLElement>("#batch-channel-empty")!.hidden =
    batchChannels.length !== 0;
  batchChannelRows.innerHTML = batchChannels.map(channel => {
    const state = channel.withdrawRequestedAt > 0
      ? "提现保护期"
      : channel.pending ? "调用处理中" : "正常";
    return `
      <tr>
        <td><strong>${escapeHTML(shortAddress(channel.payer))}</strong><small>${escapeHTML(channel.channelId)}</small></td>
        <td><strong>${escapeHTML(formatUSDC(channel.balance))} USDC</strong><small>用户可用 ${escapeHTML(formatUSDC(channel.availableAmount))}</small></td>
        <td><strong>${escapeHTML(formatUSDC(channel.chargedCumulativeAmount))} USDC</strong><small>已 Claim ${escapeHTML(formatUSDC(channel.totalClaimed))}</small></td>
        <td><strong>${escapeHTML(formatUSDC(channel.claimableAmount))} USDC</strong></td>
        <td><span class="status-chip ${channel.withdrawRequestedAt > 0 ? "archived" : "published"}">${state}</span></td>
      </tr>`;
  }).join("");
  if (!document.querySelector<HTMLElement>("#settlement-view")!.hidden) {
    document.querySelector("#summary-count")!.textContent =
      `${formatUSDC(sumAtomic(batchChannels.map(channel => channel.claimableAmount)))} USDC TO CLAIM`;
  }
}

const PERMISSION_LABELS: Record<AdminPermission, string> = {
  products: "服务审核查看",
  product_edit: "服务发布 / 下架",
  integrations: "供应商配置",
  orders: "订单查询",
  revenue: "收入与资金结算",
  providers: "服务商申请查看",
  provider_review: "服务商审核 / 开户",
  access: "权限设置",
  audit: "操作日志",
};

const AUDIT_FIELD_LABELS: Record<string, string> = {
  name: "名称",
  email: "邮箱",
  enabled: "启用状态",
  permissions: "菜单权限",
  password: "密码",
  slug: "Slug",
  description: "描述",
  serviceUrl: "目标 API",
  method: "请求方法",
  providerType: "服务提供方",
  providerAction: "Provider Action",
  requestEncoding: "请求编码",
  requestSchema: "入参定义",
  requestTemplate: "请求模板",
  responseSchema: "返回展示",
  paymentMode: "旧版默认支付模式",
  network: "支付网络",
  tokenAddress: "Token 地址",
  priceUsdcAtomic: "单次调用价格",
  costUsdcAtomic: "单次调用成本",
  receiverWallet: "收款钱包",
  refundHoldingSeconds: "退款等待秒数",
  status: "产品状态",
  environment: "环境",
  kytBaseUrl: "KYT URL",
  spanBaseUrl: "SPAN URL",
  leiBaseUrl: "LEI URL",
  usernameConfigured: "Username 配置",
  passwordConfigured: "Password 配置",
  secretConfigured: "Secret 配置",
  username: "Username",
  secret: "Secret",
  alias: "凭据别名",
  serviceType: "服务类型",
  credentials: "产品凭据",
  version: "凭据版本",
};

function renderUsers(): void {
  document.querySelector<HTMLElement>("#user-empty")!.hidden = adminUsers.length !== 0;
  userRows.innerHTML = adminUsers.map(user => `
    <tr>
      <td><strong>${escapeHTML(user.name)}</strong><small>${escapeHTML(user.email)}</small></td>
      <td><span class="status-chip ${user.enabled ? "published" : "archived"}">${user.enabled ? "ENABLED" : "DISABLED"}</span></td>
      <td><small class="permission-summary">${user.permissions.map(permission =>
        escapeHTML(PERMISSION_LABELS[permission]),
      ).join(" · ") || "无权限"}</small></td>
      <td><code>${escapeHTML(new Date(user.updatedAt).toLocaleString("zh-CN"))}</code></td>
      <td class="row-actions"><button type="button" data-edit-user="${escapeHTML(user.id)}">编辑</button></td>
    </tr>
  `).join("");
  userRows.querySelectorAll<HTMLButtonElement>("[data-edit-user]").forEach(button => {
    button.addEventListener("click", () => editUser(button.dataset.editUser!));
  });
  if (!document.querySelector<HTMLElement>("#access-view")!.hidden) {
    document.querySelector("#summary-count")!.textContent = `${adminUsers.length} USERS`;
  }
}

function resetUserForm(): void {
  userForm.reset();
  (document.querySelector<HTMLInputElement>("#user-id")!).value = "";
  (document.querySelector<HTMLInputElement>("#user-enabled")!).checked = true;
  document.querySelector("#user-editor-title")!.textContent = "NEW ADMIN USER";
  document.querySelector("#user-editor-state")!.textContent = "NEW";
  document.querySelector<HTMLElement>("#user-status")!.hidden = true;
}

function editUser(id: string): void {
  const user = adminUsers.find(item => item.id === id);
  if (!user) return;
  (document.querySelector<HTMLInputElement>("#user-id")!).value = user.id;
  (document.querySelector<HTMLInputElement>("#user-name")!).value = user.name;
  (document.querySelector<HTMLInputElement>("#user-email")!).value = user.email;
  (document.querySelector<HTMLInputElement>("#user-password")!).value = "";
  (document.querySelector<HTMLInputElement>("#user-enabled")!).checked = user.enabled;
  document.querySelectorAll<HTMLInputElement>("[name=user-permission]").forEach(input => {
    input.checked = user.permissions.includes(input.value as AdminPermission);
  });
  document.querySelector("#user-editor-title")!.textContent = "EDIT ADMIN USER";
  document.querySelector("#user-editor-state")!.textContent =
    currentUser?.id === user.id ? "CURRENT USER" : "EXISTING";
  document.querySelector<HTMLElement>("#user-status")!.hidden = true;
  userForm.scrollIntoView({ behavior: "smooth", block: "start" });
}

function auditValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (Array.isArray(value)) {
    return value.map(item => PERMISSION_LABELS[item as AdminPermission] || String(item)).join("、");
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function renderAuditLogs(): void {
  document.querySelector<HTMLElement>("#audit-empty")!.hidden = auditLogs.length !== 0;
  auditRows.innerHTML = auditLogs.map(log => {
    const changes = Object.entries(log.changes);
    return `
      <tr>
        <td><strong>${escapeHTML(log.actorName)}</strong><small>${escapeHTML(log.actorEmail)}</small></td>
        <td><strong>${escapeHTML(log.action)} · ${escapeHTML(log.resourceType)}</strong><small>${escapeHTML(log.resourceName)} · ${escapeHTML(log.resourceId)}</small></td>
        <td>
          <details class="audit-details">
            <summary>${changes.length} 个字段变更</summary>
            <div>${changes.map(([field, change]) => `
              <p><b>${escapeHTML(AUDIT_FIELD_LABELS[field] || field)}</b><span>${escapeHTML(auditValue(change.before))}</span><i>→</i><span>${escapeHTML(auditValue(change.after))}</span></p>
            `).join("") || "<p><span>无字段值变更</span></p>"}</div>
          </details>
        </td>
        <td><code>${escapeHTML(new Date(log.createdAt).toLocaleString("zh-CN"))}</code></td>
      </tr>
    `;
  }).join("");
  const totalPages = Math.max(1, auditPagination.totalPages);
  document.querySelector("#audit-page-label")!.textContent =
    `第 ${auditPagination.page} / ${totalPages} 页 · 共 ${auditPagination.total} 条`;
  (document.querySelector<HTMLButtonElement>("#audit-prev-page")!).disabled = auditPagination.page <= 1;
  (document.querySelector<HTMLButtonElement>("#audit-next-page")!).disabled =
    auditPagination.totalPages === 0 || auditPagination.page >= auditPagination.totalPages;
  if (!document.querySelector<HTMLElement>("#audit-view")!.hidden) {
    document.querySelector("#summary-count")!.textContent = `${auditPagination.total} LOGS`;
  }
}

function resetProductForm(): void {
  productForm.reset();
  productProviderBinding = undefined;
  (document.querySelector<HTMLInputElement>("#product-id")!).value = "";
  (document.querySelector<HTMLSelectElement>("#product-network")!).value = BASE_SEPOLIA_NETWORK;
  (document.querySelector<HTMLInputElement>("#product-price")!).value = "0.001";
  (document.querySelector<HTMLInputElement>("#product-cost")!).value = "0";
  (document.querySelector<HTMLInputElement>("#product-token")!).value = BASE_SEPOLIA_USDC;
  (document.querySelector<HTMLSelectElement>("#product-provider")!).value = "GENERIC";
  (document.querySelector<HTMLInputElement>("#product-action")!).value = "";
  (document.querySelector<HTMLSelectElement>("#product-request-encoding")!).value = "JSON";
  renderParameterDefinitions([]);
  (document.querySelector<HTMLSelectElement>("#response-mode")!).value = "JSON";
  (document.querySelector<HTMLInputElement>("#response-root")!).value = "";
  (document.querySelector<HTMLInputElement>("#response-summary-root")!).value = "";
  renderResponseFields([]);
  renderResponseSummaryFields([]);
  document.querySelector("#editor-title")!.textContent = "NEW PRODUCT";
  document.querySelector("#editor-version")!.textContent = "DRAFT";
  document.querySelector<HTMLElement>("#product-status")!.hidden = true;
  document.querySelector<HTMLElement>("#product-credential-section")!.hidden = true;
  document.querySelector<HTMLElement>("#product-credential-status")!.hidden = true;
  updatePaymentReadiness();
  updateProviderFields();
}

function editProduct(id: string): void {
  if (!hasPermission("product_edit")) return;
  const product = products.find(item => item.id === id);
  if (!product) return;
  (document.querySelector<HTMLInputElement>("#product-id")!).value = product.id;
  (document.querySelector<HTMLInputElement>("#product-name")!).value = product.name;
  (document.querySelector<HTMLInputElement>("#product-slug")!).value = product.slug;
  (document.querySelector<HTMLTextAreaElement>("#product-description")!).value = product.description;
  (document.querySelector<HTMLSelectElement>("#product-method")!).value = product.method;
  (document.querySelector<HTMLInputElement>("#product-url")!).value = product.serviceUrl;
  (document.querySelector<HTMLSelectElement>("#product-provider")!).value = product.providerType;
  (document.querySelector<HTMLInputElement>("#product-action")!).value = product.providerAction || "";
  (document.querySelector<HTMLSelectElement>("#product-request-encoding")!).value = product.requestEncoding || "JSON";
  renderParameterDefinitions(parseParameterDefinitions(product.requestSchema));
  const responseDefinition = parseResponseDefinition(product.responseSchema);
  (document.querySelector<HTMLSelectElement>("#response-mode")!).value = responseDefinition.mode;
  (document.querySelector<HTMLInputElement>("#response-root")!).value = responseDefinition.root || "";
  (document.querySelector<HTMLInputElement>("#response-summary-root")!).value = responseDefinition.summaryRoot || "";
  renderResponseFields(responseDefinition.fields || []);
  renderResponseSummaryFields(responseDefinition.summaryFields || []);
  (document.querySelector<HTMLSelectElement>("#product-network")!).value = product.network;
  (document.querySelector<HTMLInputElement>("#product-price")!).value = formatUSDC(product.priceUsdcAtomic);
  (document.querySelector<HTMLInputElement>("#product-cost")!).value = formatUSDC(product.costUsdcAtomic || "0");
  (document.querySelector<HTMLInputElement>("#product-token")!).value = product.tokenAddress;
  (document.querySelector<HTMLInputElement>("#product-wallet")!).value = product.receiverWallet;
  updatePaymentReadiness();
  updateProviderFields();
  document.querySelector("#editor-title")!.textContent = "EDIT PRODUCT";
  document.querySelector("#editor-version")!.textContent = `${product.status} · V${product.version}`;
  document.querySelector<HTMLElement>("#product-status")!.hidden = true;
  if (product.providerType === "WINSHIELD" && hasPermission("integrations")) {
    void loadProductProviderBinding(product.id);
  } else {
    productProviderBinding = undefined;
    document.querySelector<HTMLElement>("#product-credential-section")!.hidden = true;
  }
  productForm.scrollIntoView({ behavior: "smooth", block: "start" });
}

function productPayload(): Record<string, unknown> {
  const definitions = readParameterDefinitions();
  const responseDefinition = readResponseDefinition();
  const providerType = (document.querySelector<HTMLSelectElement>("#product-provider")!).value;
  return {
    name: (document.querySelector<HTMLInputElement>("#product-name")!).value.trim(),
    slug: (document.querySelector<HTMLInputElement>("#product-slug")!).value.trim(),
    description: (document.querySelector<HTMLTextAreaElement>("#product-description")!).value.trim(),
    method: (document.querySelector<HTMLSelectElement>("#product-method")!).value,
    serviceUrl: providerType === "GENERIC"
      ? (document.querySelector<HTMLInputElement>("#product-url")!).value.trim()
      : "",
    providerType,
    providerAction: (document.querySelector<HTMLInputElement>("#product-action")!).value.trim(),
    requestEncoding: (document.querySelector<HTMLSelectElement>("#product-request-encoding")!).value,
    requestSchema: JSON.stringify(definitions),
    requestTemplate: JSON.stringify(defaultTemplate(definitions)),
    responseSchema: JSON.stringify(responseDefinition),
    paymentMode: "EXACT",
    network: (document.querySelector<HTMLSelectElement>("#product-network")!).value,
    priceUsdcAtomic: usdcToAtomic((document.querySelector<HTMLInputElement>("#product-price")!).value),
    costUsdcAtomic: usdcToAtomic((document.querySelector<HTMLInputElement>("#product-cost")!).value),
    tokenAddress: (document.querySelector<HTMLInputElement>("#product-token")!).value.trim(),
    receiverWallet: (document.querySelector<HTMLInputElement>("#product-wallet")!).value.trim(),
  };
}

function updatePaymentReadiness(): void {
  const network = (document.querySelector<HTMLSelectElement>("#product-network")!).value;
  const tokenAddress = (document.querySelector<HTMLInputElement>("#product-token")!).value.trim();
  const ready = network === BASE_SEPOLIA_NETWORK &&
    tokenAddress.toLowerCase() === BASE_SEPOLIA_USDC.toLowerCase();
  const state = document.querySelector<HTMLElement>(".settlement-state")!;
  state.textContent = ready ? "SUPPORTED" : "CONFIG ONLY";
  state.classList.toggle("config-only", !ready);
  document.querySelector("#product-payment-note")!.textContent = ready
    ? "上架后同时支持 x402 Exact 单次结算和 Batch Settlement 共享通道余额调用；收款钱包必须与当前部署的通道收款钱包一致。"
    : "该网络或 Token 可保存为产品草稿；当前支付网关尚不能上架或发起付款。";
}

function updateProviderFields(): void {
  const isWintranx =
    (document.querySelector<HTMLSelectElement>("#product-provider")!).value === "WINSHIELD";
  const genericURLField = document.querySelector<HTMLElement>("#generic-url-field")!;
  const productURL = document.querySelector<HTMLInputElement>("#product-url")!;
  genericURLField.hidden = isWintranx;
  productURL.required = !isWintranx;
  if (isWintranx) {
    (document.querySelector<HTMLSelectElement>("#product-method")!).value = "POST";
    (document.querySelector<HTMLSelectElement>("#product-request-encoding")!).value = "JSON";
  }
}

function formatJSONText(value: string): string {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

async function loadProducts(): Promise<void> {
  const body = await api<{ products: Product[] }>("/v1/admin/products");
  products = body.products;
  renderProducts();
}

async function loadOrderProducts(): Promise<void> {
  const body = await api<{ products: Array<{ id: string; name: string }> }>("/v1/admin/order-products");
  orderFilterProducts = body.products;
  renderOrderProductFilter();
}

function renderProductProviderBinding(): void {
  const section = document.querySelector<HTMLElement>("#product-credential-section")!;
  const productId = (document.querySelector<HTMLInputElement>("#product-id")!).value;
  const provider = (document.querySelector<HTMLSelectElement>("#product-provider")!).value;
  section.hidden = !productId || provider !== "WINSHIELD" || !hasPermission("integrations");
  if (section.hidden) return;
  const binding = productProviderBinding;
  if (!binding) {
    document.querySelector("#product-credential-state")!.textContent = "NOT CONFIGURED";
    document.querySelector("#product-credential-meta")!.textContent = "尚未创建凭据绑定";
    (document.querySelector<HTMLInputElement>("#product-credential-alias")!).value = "";
    (document.querySelector<HTMLInputElement>("#product-credential-endpoint")!).value = "";
    (document.querySelector<HTMLInputElement>("#product-credential-enabled")!).checked = false;
    return;
  }
  (document.querySelector<HTMLInputElement>("#product-credential-alias")!).value = binding.alias;
  (document.querySelector<HTMLInputElement>("#product-credential-endpoint")!).value = binding.endpointUrl;
  (document.querySelector<HTMLInputElement>("#product-credential-enabled")!).checked = binding.enabled;
  const complete =
    binding.endpointUrl !== "" && binding.hasUsername && binding.hasPassword && binding.hasSecret;
  document.querySelector("#product-credential-state")!.textContent =
    !binding.enabled ? "DISABLED" : complete ? "READY" : "INCOMPLETE";
  const testedAt = binding.lastTestedAt && new Date(binding.lastTestedAt).getTime() > 0
    ? new Date(binding.lastTestedAt).toLocaleString("zh-CN")
    : "未测试";
  document.querySelector("#product-credential-meta")!.textContent =
    `${binding.serviceType} · ${binding.providerAction || "—"} · V${binding.version} · ${binding.lastTestStatus} / ${testedAt} · URL ${binding.endpointUrl ? "✓" : "—"} · Username ${binding.hasUsername ? "✓" : "—"} · Password ${binding.hasPassword ? "✓" : "—"} · Secret ${binding.hasSecret ? "✓" : "—"}`;
  (document.querySelector<HTMLInputElement>("#product-credential-username")!).value = "";
  (document.querySelector<HTMLInputElement>("#product-credential-password")!).value = "";
  (document.querySelector<HTMLInputElement>("#product-credential-secret")!).value = "";
}

async function loadProductProviderBinding(productId: string): Promise<void> {
  try {
    const body = await api<{ binding: ProductProviderBinding }>(
      `/v1/admin/products/${productId}/provider-binding`,
    );
    productProviderBinding = body.binding;
    renderProductProviderBinding();
  } catch (error) {
    productProviderBinding = undefined;
    renderProductProviderBinding();
    showStatus(
      "product-credential-status",
      error instanceof Error ? error.message : "读取产品凭据失败",
      true,
    );
  }
}

async function saveProductProviderBinding(): Promise<void> {
  const productId = (document.querySelector<HTMLInputElement>("#product-id")!).value;
  if (!productId || !hasPermission("integrations")) return;
  const username = (document.querySelector<HTMLInputElement>("#product-credential-username")!).value;
  const password = (document.querySelector<HTMLInputElement>("#product-credential-password")!).value;
  const secret = (document.querySelector<HTMLInputElement>("#product-credential-secret")!).value;
  const endpointUrl =
    (document.querySelector<HTMLInputElement>("#product-credential-endpoint")!).value.trim();
  if (!endpointUrl) {
    showStatus("product-credential-status", "请填写当前部署使用的产品 API URL。", true);
    return;
  }
  const provided = [username, password, secret].filter(value => value.trim() !== "").length;
  if (provided !== 0 && provided !== 3) {
    showStatus("product-credential-status", "更新凭据时 Username、Password、Secret Key 必须同时填写。", true);
    return;
  }
  try {
    const body = await api<{ binding: ProductProviderBinding }>(
      `/v1/admin/products/${productId}/provider-binding`,
      {
        method: "PUT",
        body: JSON.stringify({
          alias: (document.querySelector<HTMLInputElement>("#product-credential-alias")!).value.trim(),
          endpointUrl,
          enabled: (document.querySelector<HTMLInputElement>("#product-credential-enabled")!).checked,
          username,
          password,
          secret,
        }),
      },
    );
    productProviderBinding = body.binding;
    renderProductProviderBinding();
    showStatus(
      "product-credential-status",
      provided === 3 ? "三项凭据已作为同一版本加密轮换。" : "当前部署配置已更新，凭据保持原值。",
    );
  } catch (error) {
    showStatus(
      "product-credential-status",
      error instanceof Error ? error.message : "保存产品凭据失败",
      true,
    );
  }
}

function renderSimulationProviderSummary(product: Product): void {
  const summary = document.querySelector<HTMLElement>("#simulation-provider-summary")!;
  if (product.providerType !== "WINSHIELD") {
    summary.hidden = false;
    summary.innerHTML = `<strong>GENERIC HTTP</strong><span>${escapeHTML(product.method)} · ${escapeHTML(product.serviceUrl)}</span>`;
    return;
  }
  const binding = simulationProviderBinding;
  if (!binding) {
    summary.hidden = true;
    summary.innerHTML = "";
    return;
  }
  summary.hidden = false;
  summary.innerHTML = `
    <strong>CURRENT DEPLOYMENT · ${escapeHTML(binding.serviceType)} · ${escapeHTML(binding.providerAction || "—")}</strong>
    <span>${escapeHTML(binding.endpointUrl || "未配置 URL")}</span>
  `;
}

async function openSimulation(id: string, trigger?: HTMLElement): Promise<void> {
  const product = products.find(item => item.id === id);
  if (!product) return;
  simulationTrigger = trigger || document.activeElement as HTMLElement;
  simulationScrollY = window.scrollY;
  (document.querySelector<HTMLInputElement>("#simulation-product-id")!).value = id;
  renderRuntimeForm(simulationFields, parseParameterDefinitions(product.requestSchema), product.requestTemplate || "{}");
  document.querySelector("#simulation-title")!.textContent = `ADMIN TEST · ${product.name}`;
  simulationRawResult = undefined;
  simulationResponseSchema = product.responseSchema || "";
  simulationResultAvailable = false;
  simulationShowingRaw = false;
  renderSimulationResultView();
  const chargeConfirm = document.querySelector<HTMLElement>("#provider-charge-confirm")!;
  const chargeCheckbox = document.querySelector<HTMLInputElement>("#confirm-provider-charge")!;
  chargeConfirm.hidden = product.providerType !== "WINSHIELD";
  chargeCheckbox.checked = false;
  simulationProviderBinding = undefined;
  if (product.providerType === "WINSHIELD" && hasPermission("integrations")) {
    try {
      const body = await api<{ binding: ProductProviderBinding }>(
        `/v1/admin/products/${product.id}/provider-binding`,
      );
      simulationProviderBinding = body.binding;
    } catch (error) {
      document.querySelector("#simulation-result")!.textContent =
        error instanceof Error ? error.message : "读取当前部署配置失败";
    }
  }
  renderSimulationProviderSummary(product);
  simulationModal.hidden = false;
  document.body.classList.add("modal-open");
  document.querySelector<HTMLButtonElement>("#close-simulation")!.focus();
}

function closeSimulation(): void {
  simulationModal.hidden = true;
  document.body.classList.remove("modal-open");
  simulationTrigger?.focus({ preventScroll: true });
  window.scrollTo(0, simulationScrollY);
  simulationTrigger = null;
}

async function loadOrders(page = orderPagination.page): Promise<void> {
  const query = new URLSearchParams({
    page: String(page),
    pageSize: orderPageSize.value,
  });
  if (orderProductFilter.value) query.set("productId", orderProductFilter.value);
  if (orderStatusFilter.value) query.set("status", orderStatusFilter.value);
  if (orderDateFrom.value) query.set("dateFrom", localDateBoundary(orderDateFrom.value, false));
  if (orderDateTo.value) query.set("dateTo", localDateBoundary(orderDateTo.value, true));
  const body = await api<{
    orders: Order[];
    pagination: OrderPagination;
    summary: OrderSummary;
  }>(`/v1/admin/orders?${query}`);
  orders = body.orders;
  orderPagination = body.pagination;
  renderOrders();
}

async function loadNotificationSubscriptions(page = subscriptionPagination.page): Promise<void> {
  const query = new URLSearchParams({
    page: String(page),
    pageSize: subscriptionPageSize.value,
  });
  if (subscriptionProductFilter.value) query.set("productId", subscriptionProductFilter.value);
  if (subscriptionStatusFilter.value) query.set("status", subscriptionStatusFilter.value);
  if (subscriptionWalletFilter.value.trim()) query.set("wallet", subscriptionWalletFilter.value.trim());
  const body = await api<{
    subscriptions: NotificationSubscription[];
    pagination: OrderPagination;
  }>(`/v1/admin/notification-subscriptions?${query}`);
  notificationSubscriptions = body.subscriptions;
  subscriptionPagination = body.pagination;
  renderNotificationSubscriptions();
}

function localDateBoundary(value: string, endOfDay: boolean): string {
  const [year, month, day] = value.split("-").map(Number);
  const boundary = endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day);
  return boundary.toISOString();
}

async function loadRevenueReport(): Promise<void> {
  const query = new URLSearchParams();
  if (revenueDateFrom.value) query.set("dateFrom", localDateBoundary(revenueDateFrom.value, false));
  if (revenueDateTo.value) query.set("dateTo", localDateBoundary(revenueDateTo.value, true));
  const body = await api<{
    summary: OrderSummary;
    byCustomer: OrderCustomerSummary[];
  }>(`/v1/admin/revenue?${query}`);
  revenueSummary = body.summary;
  revenueByCustomer = body.byCustomer;
  renderRevenueReport();
}

async function loadBatchChannels(): Promise<void> {
  try {
    const body = await api<{ channels: BatchChannel[] }>("/v1/admin/batch/channels");
    batchChannels = body.channels;
    renderBatchChannels();
    document.querySelector<HTMLElement>("#batch-funds-status")!.hidden = true;
  } catch (error) {
    batchChannels = [];
    renderBatchChannels();
    showStatus(
      "batch-funds-status",
      error instanceof Error ? error.message : "读取充值通道失败",
      true,
    );
  }
}

async function loadBatchSettings(): Promise<void> {
  try {
    const body = await api<{ settings: BatchSettings }>("/v1/admin/batch/settings");
    batchSettings = body.settings;
    batchRefundHoldingMinutes.value = String(
      Math.max(1, Math.round(batchSettings.refundHoldingSeconds / 60)),
    );
    document.querySelector<HTMLElement>("#batch-settings-status")!.hidden = true;
  } catch (error) {
    showStatus(
      "batch-settings-status",
      error instanceof Error ? error.message : "读取退款等待设置失败",
      true,
    );
  }
}

async function saveBatchSettings(): Promise<void> {
  const minutes = Number.parseInt(batchRefundHoldingMinutes.value, 10);
  if (!Number.isInteger(minutes) || minutes < 1 || minutes > 43200) {
    showStatus("batch-settings-status", "退款等待时间必须是 1 到 43200 分钟。", true);
    return;
  }
  try {
    const body = await api<{ settings: BatchSettings }>("/v1/admin/batch/settings", {
      method: "PUT",
      body: JSON.stringify({ refundHoldingSeconds: minutes * 60 }),
    });
    batchSettings = body.settings;
    showStatus(
      "batch-settings-status",
      `已保存为 ${minutes} 分钟，仅影响之后新提交的退款申请。`,
    );
  } catch (error) {
    showStatus(
      "batch-settings-status",
      error instanceof Error ? error.message : "保存退款等待设置失败",
      true,
    );
  }
}

async function runBatchAction(
  action: "claim" | "settle" | "claim-and-settle",
  button: HTMLButtonElement,
): Promise<void> {
  const labels = {
    claim: "Claim",
    settle: "Settle",
    "claim-and-settle": "Claim 并结算",
  };
  const original = button.textContent || labels[action];
  button.disabled = true;
  button.textContent = "处理中…";
  try {
    await api(`/v1/admin/batch/${action}`, { method: "POST" });
    await loadBatchChannels();
    showStatus("batch-funds-status", `${labels[action]} 已提交完成。`);
  } catch (error) {
    showStatus(
      "batch-funds-status",
      error instanceof Error ? error.message : `${labels[action]} 失败`,
      true,
    );
  } finally {
    button.disabled = false;
    button.textContent = original;
  }
}

async function loadAdminUsers(): Promise<void> {
  const body = await api<{ users: AdminUser[] }>("/v1/admin/users");
  adminUsers = body.users;
  renderUsers();
}

async function loadAuditLogs(page = auditPagination.page): Promise<void> {
  const query = new URLSearchParams({
    page: String(page),
    pageSize: auditPageSize.value,
  });
  if (auditActorFilter.value.trim()) query.set("actor", auditActorFilter.value.trim());
  if (auditResourceFilter.value) query.set("resourceType", auditResourceFilter.value);
  if (auditDateFrom.value) query.set("dateFrom", localDateBoundary(auditDateFrom.value, false));
  if (auditDateTo.value) query.set("dateTo", localDateBoundary(auditDateTo.value, true));
  const body = await api<{ logs: AuditLog[]; pagination: OrderPagination }>(
    `/v1/admin/audit-logs?${query}`,
  );
  auditLogs = body.logs;
  auditPagination = body.pagination;
  renderAuditLogs();
}

function validateDateRange(
  form: HTMLFormElement,
  dateFrom: HTMLInputElement,
  dateTo: HTMLInputElement,
): boolean {
  const invalidRange = dateFrom.value !== "" && dateTo.value !== "" && dateFrom.value > dateTo.value;
  dateTo.setCustomValidity(invalidRange ? "结束日期不能早于开始日期" : "");
  return form.reportValidity();
}

async function toggleProduct(id: string): Promise<void> {
  if (!hasPermission("product_edit")) return;
  const product = products.find(item => item.id === id);
  if (!product) return;
  const hasPendingVersion = product.workingVersion?.status === "PENDING_REVIEW";
  if (product.status !== "PENDING_REVIEW" && product.status !== "PUBLISHED") return;
  const action = product.status === "PENDING_REVIEW" || hasPendingVersion ? "publish" : "unpublish";
  try {
    await api(`/v1/admin/products/${id}/${action}`, { method: "POST" });
    await loadProducts();
  } catch (error) {
    window.alert(error instanceof Error ? error.message : "更新状态失败");
  }
}

loginForm.addEventListener("submit", async event => {
  event.preventDefault();
  const email = document.querySelector<HTMLInputElement>("#login-email")!.value.trim();
  const password = document.querySelector<HTMLInputElement>("#login-password")!.value;
  try {
    const body = await api<{ token: string; user: AdminUser }>("/v1/admin/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    token = body.token;
    currentUser = body.user;
    sessionStorage.setItem("bizx402-admin-token", token);
    showAdmin();
  } catch (error) {
    showStatus("login-status", error instanceof Error ? error.message : "登录失败", true);
  }
});

productForm.addEventListener("submit", async event => {
  event.preventDefault();
  showStatus("product-status", "服务定义已迁移到 Provider Portal。", true);
});

userForm.addEventListener("submit", async event => {
  event.preventDefault();
  const id = document.querySelector<HTMLInputElement>("#user-id")!.value;
  const password = document.querySelector<HTMLInputElement>("#user-password")!.value;
  if (!id && password.length < 8) {
    showStatus("user-status", "新用户密码至少需要 8 个字符。", true);
    return;
  }
  const permissions = Array.from(
    document.querySelectorAll<HTMLInputElement>("[name=user-permission]:checked"),
  ).map(input => input.value as AdminPermission);
  try {
    const body = await api<{ user: AdminUser }>(id ? `/v1/admin/users/${id}` : "/v1/admin/users", {
      method: id ? "PUT" : "POST",
      body: JSON.stringify({
        name: document.querySelector<HTMLInputElement>("#user-name")!.value.trim(),
        email: document.querySelector<HTMLInputElement>("#user-email")!.value.trim(),
        password,
        enabled: document.querySelector<HTMLInputElement>("#user-enabled")!.checked,
        permissions,
      }),
    });
    if (currentUser?.id === body.user.id) currentUser = body.user;
    showStatus("user-status", id ? "用户与菜单权限已更新。" : "后台用户已创建。");
    await loadAdminUsers();
    if (!id) resetUserForm();
  } catch (error) {
    showStatus("user-status", error instanceof Error ? error.message : "保存用户失败", true);
  }
});

document.querySelectorAll<HTMLInputElement>("[name=user-permission]").forEach(input => {
  input.addEventListener("change", () => {
    const productView = document.querySelector<HTMLInputElement>(
      "[name=user-permission][value=products]",
    )!;
    const productEdit = document.querySelector<HTMLInputElement>(
      "[name=user-permission][value=product_edit]",
    )!;
    const providerConfig = document.querySelector<HTMLInputElement>(
      "[name=user-permission][value=integrations]",
    )!;
    if (input === productEdit && productEdit.checked) productView.checked = true;
    if (input === providerConfig && providerConfig.checked) productView.checked = true;
    if (input === productView && !productView.checked) {
      productEdit.checked = false;
      providerConfig.checked = false;
    }
  });
});

document.querySelector("#save-product-credential")!.addEventListener("click", () => {
  void saveProductProviderBinding();
});

document.querySelector<HTMLSelectElement>("#product-provider")!.addEventListener("change", () => {
  updateProviderFields();
  const productId = (document.querySelector<HTMLInputElement>("#product-id")!).value;
  if (productId &&
    (document.querySelector<HTMLSelectElement>("#product-provider")!).value === "WINSHIELD" &&
    hasPermission("integrations")) {
    void loadProductProviderBinding(productId);
  } else {
    document.querySelector<HTMLElement>("#product-credential-section")!.hidden = true;
  }
});

document.querySelector<HTMLSelectElement>("#product-network")!.addEventListener(
  "change",
  updatePaymentReadiness,
);
document.querySelector<HTMLInputElement>("#product-token")!.addEventListener(
  "input",
  updatePaymentReadiness,
);
simulationForm.addEventListener("submit", async event => {
  event.preventDefault();
  const id = (document.querySelector<HTMLInputElement>("#simulation-product-id")!).value;
  const product = products.find(item => item.id === id);
  const result = document.querySelector("#simulation-result")!;
  try {
    const confirmed = (document.querySelector<HTMLInputElement>("#confirm-provider-charge")!).checked;
    if (product?.providerType === "WINSHIELD" && !confirmed) {
      throw new Error("请先确认该 Winshield 测试可能消耗供应商额度或产生费用。");
    }
    const input = await collectRuntimeValues(simulationFields, parseParameterDefinitions(product?.requestSchema || "[]"));
    result.textContent = "调用中…";
    const headers: Record<string, string> = {
      "x-confirm-provider-charge": confirmed ? "true" : "false",
    };
    const body = await api<{ simulation: Simulation; result: unknown }>(`/v1/admin/products/${id}/simulate`, {
      method: "POST",
      headers,
      body: JSON.stringify(input),
    });
    simulationRawResult = body.result;
    simulationResponseSchema = product?.responseSchema || "";
    simulationResultAvailable = true;
    simulationShowingRaw = false;
    renderSimulationResultView();
  } catch (error) {
    result.textContent = error instanceof Error ? error.message : "测试调用失败";
  }
});

document.querySelector("#new-product")!.addEventListener("click", () => {
  window.alert("服务定义已迁移到 Provider Portal。");
});
document.querySelector("#add-parameter")!.addEventListener("click", () => {
  const definitions = readParameterDefinitions();
  definitions.push({ name: "", label: "", type: "string", required: false });
  renderParameterDefinitions(definitions);
  parameterRows.querySelector<HTMLInputElement>("[data-parameter-row]:last-child [data-param=name]")?.focus();
});
document.querySelector("#add-response-field")!.addEventListener("click", () => {
  const fields = readResponseDefinition().fields || [];
  fields.push({ path: "", label: "", format: "text" });
  renderResponseFields(fields);
  responseFieldRows.querySelector<HTMLInputElement>("[data-response-field-row]:last-child [data-response=path]")?.focus();
});
document.querySelector("#add-response-summary-field")!.addEventListener("click", () => {
  const fields = readResponseDefinition().summaryFields || [];
  fields.push({ path: "", label: "", format: "text" });
  renderResponseSummaryFields(fields);
  responseSummaryFieldRows.querySelector<HTMLInputElement>("[data-response-summary-field-row]:last-child [data-response=path]")?.focus();
});
document.querySelector("#reset-product")!.addEventListener("click", resetProductForm);
document.querySelector("#reset-user")!.addEventListener("click", resetUserForm);
document.querySelector("#close-simulation")!.addEventListener("click", closeSimulation);
document.querySelector("#simulation-backdrop")!.addEventListener("click", closeSimulation);
document.addEventListener("keydown", event => {
  if (event.key === "Escape" && !simulationModal.hidden) closeSimulation();
});
document.querySelector("#logout-button")!.addEventListener("click", showLogin);
document.querySelector("#toggle-raw-response")!.addEventListener("click", () => {
  if (!simulationResultAvailable) return;
  simulationShowingRaw = !simulationShowingRaw;
  renderSimulationResultView();
});
document.querySelector<HTMLFormElement>("#order-filters")!.addEventListener("submit", event => {
  event.preventDefault();
  if (!validateDateRange(event.currentTarget as HTMLFormElement, orderDateFrom, orderDateTo)) return;
  void loadOrders(1);
});
document.querySelector<HTMLFormElement>("#subscription-filters")!.addEventListener("submit", event => {
  event.preventDefault();
  void loadNotificationSubscriptions(1);
});
orderDateFrom.addEventListener("input", () => orderDateTo.setCustomValidity(""));
orderDateTo.addEventListener("input", () => orderDateTo.setCustomValidity(""));
document.querySelector<HTMLFormElement>("#revenue-filters")!.addEventListener("submit", event => {
  event.preventDefault();
  if (!validateDateRange(event.currentTarget as HTMLFormElement, revenueDateFrom, revenueDateTo)) return;
  void loadRevenueReport();
});
revenueDateFrom.addEventListener("input", () => revenueDateTo.setCustomValidity(""));
revenueDateTo.addEventListener("input", () => revenueDateTo.setCustomValidity(""));
document.querySelector<HTMLFormElement>("#audit-filters")!.addEventListener("submit", event => {
  event.preventDefault();
  if (!validateDateRange(event.currentTarget as HTMLFormElement, auditDateFrom, auditDateTo)) return;
  void loadAuditLogs(1);
});
auditDateFrom.addEventListener("input", () => auditDateTo.setCustomValidity(""));
auditDateTo.addEventListener("input", () => auditDateTo.setCustomValidity(""));
document.querySelector("#order-prev-page")!.addEventListener("click", () => {
  if (orderPagination.page > 1) void loadOrders(orderPagination.page - 1);
});
document.querySelector("#order-next-page")!.addEventListener("click", () => {
  if (orderPagination.page < orderPagination.totalPages) void loadOrders(orderPagination.page + 1);
});
document.querySelector("#subscription-prev-page")!.addEventListener("click", () => {
  if (subscriptionPagination.page > 1) {
    void loadNotificationSubscriptions(subscriptionPagination.page - 1);
  }
});
document.querySelector("#subscription-next-page")!.addEventListener("click", () => {
  if (subscriptionPagination.page < subscriptionPagination.totalPages) {
    void loadNotificationSubscriptions(subscriptionPagination.page + 1);
  }
});
document.querySelector("#audit-prev-page")!.addEventListener("click", () => {
  if (auditPagination.page > 1) void loadAuditLogs(auditPagination.page - 1);
});
document.querySelector("#audit-next-page")!.addEventListener("click", () => {
  if (auditPagination.page < auditPagination.totalPages) void loadAuditLogs(auditPagination.page + 1);
});

function adminViewFromLocation(): AdminView {
  const requested = window.location.hash.replace(/^#\//, "") as AdminView;
  if (ADMIN_VIEWS.includes(requested) && canView(requested)) return requested;
  return ADMIN_VIEWS.find(canView)!;
}

function renderAdminView(view: AdminView, refresh = true): void {
  if (!canView(view)) {
    const allowed = ADMIN_VIEWS.find(canView)!;
    window.history.replaceState(null, "", `#/${allowed}`);
    view = allowed;
  }
  document.querySelectorAll<HTMLButtonElement>(".nav-item").forEach(item => {
    item.classList.toggle("active", item.dataset.view === view);
  });
  document.querySelector<HTMLElement>("#products-view")!.hidden = view !== "products";
  document.querySelector<HTMLElement>("#providers-view")!.hidden = view !== "providers";
  document.querySelector<HTMLElement>("#orders-view")!.hidden = view !== "orders";
  document.querySelector<HTMLElement>("#subscriptions-view")!.hidden = view !== "subscriptions";
  document.querySelector<HTMLElement>("#revenue-view")!.hidden = view !== "revenue";
  document.querySelector<HTMLElement>("#settlement-view")!.hidden = view !== "settlement";
  document.querySelector<HTMLElement>("#finance-view")!.hidden = view !== "finance";
  document.querySelector<HTMLElement>("#access-view")!.hidden = view !== "access";
  document.querySelector<HTMLElement>("#audit-view")!.hidden = view !== "audit";
  document.querySelector<HTMLElement>("main.workspace")!.classList.toggle("finance-workspace", view === "finance");
  const titles: Record<AdminView, string> = {
    products: "服务审核",
    providers: "服务商入驻",
    orders: "订单查询",
    subscriptions: "通知订阅",
    revenue: "收入报表",
    settlement: "通道资金",
    finance: "财务中心",
    access: "权限设置",
    audit: "操作日志",
  };
  document.querySelector("#view-title")!.textContent = titles[view];
  document.querySelector("#summary-count")!.textContent =
    view === "products" ? `${products.length} PRODUCTS` :
    view === "providers" ? providersView.summary() :
    view === "orders" ? `${orderPagination.total} ORDERS` :
    view === "subscriptions" ? `${subscriptionPagination.total} SUBSCRIPTIONS` :
    view === "revenue" ? `${formatUSDC(revenueSummary.totalRevenueAtomic)} USDC` :
    view === "settlement" ? `${formatUSDC(sumAtomic(batchChannels.map(channel => channel.claimableAmount)))} USDC TO CLAIM` :
    view === "finance" ? financeView.summary() :
    view === "access" ? `${adminUsers.length} USERS` :
    `${auditPagination.total} LOGS`;
  document.querySelector<HTMLElement>("#new-product")!.hidden = true;
  productForm.hidden = true;
  document.querySelector<HTMLElement>("#product-readonly-note")!.hidden =
    view !== "products";
  if (refresh && view === "products") {
    void loadProducts();
  }
  if (refresh && view === "providers") void providersView.load();
  if (refresh && view === "orders") void Promise.all([loadOrderProducts(), loadOrders()]);
  if (refresh && view === "subscriptions") {
    void Promise.all([loadOrderProducts(), loadNotificationSubscriptions()]);
  }
  if (refresh && view === "revenue") void loadRevenueReport();
  if (refresh && view === "settlement") void Promise.all([
    loadBatchChannels(),
    loadBatchSettings(),
  ]);
  if (refresh && view === "finance") void financeView.load();
  if (refresh && view === "access") void loadAdminUsers();
  if (refresh && view === "audit") void loadAuditLogs();
}

document.querySelectorAll<HTMLButtonElement>(".nav-item").forEach(button => {
  button.addEventListener("click", () => {
    document.querySelector(".sidebar")!.classList.remove("nav-open");
    document.querySelector("#mobile-nav-toggle")!.setAttribute("aria-expanded", "false");
    const view = button.dataset.view as AdminView;
    const target = `#/${view}`;
    if (window.location.hash === target) renderAdminView(view);
    else window.location.hash = target;
  });
});
document.querySelector("#mobile-nav-toggle")!.addEventListener("click", event => {
  const button = event.currentTarget as HTMLButtonElement;
  const sidebar = document.querySelector(".sidebar")!;
  const open = !sidebar.classList.contains("nav-open");
  sidebar.classList.toggle("nav-open", open);
  button.setAttribute("aria-expanded", String(open));
});
window.addEventListener("hashchange", () => {
  if (!adminView.hidden) renderAdminView(adminViewFromLocation());
});

const batchClaimAndSettle = document.querySelector<HTMLButtonElement>("#batch-claim-settle")!;
batchClaimAndSettle.addEventListener(
  "click",
  () => void runBatchAction("claim-and-settle", batchClaimAndSettle),
);

document.querySelector<HTMLButtonElement>("#batch-refresh")!.addEventListener("click", event => {
  const button = event.currentTarget as HTMLButtonElement;
  const original = button.textContent;
  button.disabled = true;
  button.textContent = "刷新中…";
  void loadBatchChannels().finally(() => {
    button.disabled = false;
    button.textContent = original;
  });
});

document.querySelector<HTMLFormElement>("#batch-settings-form")!.addEventListener(
  "submit",
  event => {
    event.preventDefault();
    void saveBatchSettings();
  },
);

async function restore(): Promise<void> {
  if (!token) {
    showLogin();
    return;
  }
  try {
    const body = await api<{ user: AdminUser }>("/v1/admin/me");
    currentUser = body.user;
    showAdmin();
  } catch {
    showLogin();
  }
}

resetProductForm();
resetUserForm();
void restore();
